// @flow

import './index.scss';
import * as React from 'react';
import { observer } from 'mobx-react';
import { useTranslate } from '@insolus/react-i18n';
import { Form, Select, Radio, Checkbox } from '@insolus/ui-kit';
import entitiesStore from 'stores/entities';
import Loading from 'components/Loading';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Range from './Range';

import type { Values } from './Range';

type FormData = {|
    categoryId: string | null,
    manufacturerId: string | null,
    adConditionId: string | null,
    onlyRental: boolean
|};

type Prices = {|
    min: number,
    max: number
|};

export type SearchFormData = {|
    ...FormData,
    ...Prices
|};

type Props = {|
    categoryId: string | null,
    manufacturerId: string | null,
    adConditionId: string | null,
    onlyRental: boolean,
    priceRange: Prices,
    defaultPrices: Prices,
    onSearch: (formData: SearchFormData) => void
|};

const SearchForm = (props: Props): React.Node => {
    const {
        categoryId,
        manufacturerId,
        adConditionId,
        onlyRental,
        priceRange,
        defaultPrices,
        onSearch
    } = props;

    const { __ } = useTranslate();

    const [prices, setPrices] = React.useState<Prices>(defaultPrices);
    const [openCategory, setOpenCategory] = React.useState<boolean>(false);
    const [openManufacturer, setOpenManufacturer] = React.useState<boolean>(false);

    React.useEffect(() => {
        const fetch = async () => {
            await entitiesStore.fetch('categories');
            await entitiesStore.fetch('manufacturers');
            await entitiesStore.fetch('adConditions');
        };
        fetch();
    }, []);

    const handleSubmit = React.useCallback(
        (e: SyntheticEvent<HTMLFormElement>, formData: FormData) => {
            e.preventDefault();

            onSearch({
                ...formData,
                ...prices
            });
        },
        [onSearch, prices]
    );

    const handlePricesChange = React.useCallback((values: Values) => {
        setPrices({ min: values[0], max: values[1] });
    }, []);

    const handleIconClick = React.useCallback((name: string) => () => {
        if (name === 'category') {
            setOpenCategory(prev => !prev);
            return;
        }
        setOpenManufacturer(prev => !prev);
    }, []);

    const handleOpen = React.useCallback((name: string) => () => {
        if (name === 'category') {
            setOpenCategory(true);
            return;
        }
        setOpenManufacturer(true);
    }, []);

    const handleClose = React.useCallback((name: string) => () => {
        if (name === 'category') {
            setOpenCategory(false);
            return;
        }
        setOpenManufacturer(false);
    }, []);

    const { categories, manufacturers, adConditions } = entitiesStore.entities;
    if (!categories.fetched || !manufacturers.fetched || !adConditions.fetched) {
        return <Loading />;
    }

    return (
        <Form
            layout="vertical"
            className="SearchForm"
            role="search"
            onSubmit={handleSubmit}
        >
            <div className="SearchForm__options">
                <Radio.Group
                    name="adConditionId"
                    className="SearchForm__ad-conditions"
                    options={[{
                        label : __('all'),
                        value : ''
                    }].concat(entitiesStore.optionAdConditions)}
                    defaultValue={adConditionId ? parseInt(adConditionId, 10) : ''}
                />
                <Form.Field className="SearchForm__only-rental-field">
                    <Checkbox
                        id="only-rental"
                        name="onlyRental"
                        className="SearchForm__only-rental-input"
                        value="on"
                        defaultChecked={onlyRental}
                    />
                    <label className="SearchForm__only-rental-label" htmlFor="only-rental">
                        {__('Uniquement les locations')}
                    </label>
                </Form.Field>
            </div>
            <p className="SearchForm__intro">
                {__('find-the-right-machine.part1')} <span className="SearchForm__figure">{__('find-the-right-machine.part2')}</span> {__('find-the-right-machine.part3')}
            </p>
            <div className="Form__fields">
                <Form.Field className="SearchForm__criteria">
                    <Icon
                        type="list"
                        className="SearchForm__list-icon"
                        onClick={handleIconClick('category')}
                    />
                    <Select
                        name="categoryId"
                        options={entitiesStore.optionCategories}
                        defaultValue={categoryId}
                        placeholder={__('please-select-or-type-category')}
                        open={openCategory}
                        onOpen={handleOpen('category')}
                        onClose={handleClose('category')}
                        searchable
                        clearable
                    />
                </Form.Field>
                <Form.Field className="SearchForm__criteria">
                    <Icon
                        type="list"
                        className="SearchForm__list-icon"
                        onClick={handleIconClick('manufacturer')}
                    />
                    <Select
                        name="manufacturerId"
                        options={entitiesStore.optionManufacturers}
                        defaultValue={manufacturerId}
                        placeholder={__('please-select-or-type-manufacturer')}
                        open={openManufacturer}
                        onOpen={handleOpen('manufacturer')}
                        onClose={handleClose('manufacturer')}
                        searchable
                        clearable
                    />
                </Form.Field>
            </div>
            <div className="SearchForm__bottom">
                <Form.Item
                    label={__('price')}
                    className="SearchForm__criteria SearchForm__criteria--prices"
                >
                    <Range
                        min={priceRange.min}
                        max={priceRange.max}
                        defaultValues={[defaultPrices.min, defaultPrices.max]}
                        onChange={handlePricesChange}
                    />
                </Form.Item>
                <div className="SearchForm__submit">
                    <Button htmlType="submit" type="primary">
                        {__('execute-search')}
                    </Button>
                </div>
            </div>
        </Form>
    );
};

export default (observer(SearchForm): typeof SearchForm);
